import {
  Article,
  Award,
  BlogPost,
  BlogPostContent,
  BlogPostQuote,
  ContentPage,
  ContentPageItem,
  ContentPageItemCollection,
  ContentPageItemType,
  ReportRequest,
  SpaceHighlight,
  SuccessStory,
  Testimonial,
} from "services/entities/content";
import { callAPI } from "services/api";
import { DateTime } from "util/time";
import { getCacheItem, setCacheItem } from "util/cache";
import { getTrackingID } from "services/contact";

const TESTIMONIALS_CACHE_KEY = "content.testimonials";
const SPACE_HIGHLIGHTS_CACHE_KEY = "content.spaceHighlights";
const SUCCESS_STORIES_CACHE_KEY = "content.successStories";

function readContentPageItemCollection(data: any): ContentPageItemCollection {
  const collection: ContentPageItemCollection = {};
  for (const itemData of data) {
    const item = readContentPageItem(itemData);
    if (item !== undefined) {
      collection[itemData.code] = item;
    }
  }
  return collection;
}

function readContentPageItem(data: any): ContentPageItem | undefined {
  const type: ContentPageItemType = data.type;
  switch (type) {
    case ContentPageItemType.Object:
      return readContentPageItemCollection(data.child_items);
    case ContentPageItemType.List:
      return data.child_items.map(readContentPageItem);
    case ContentPageItemType.Image:
      return data.image ? new URL(data.image) : undefined;
    case ContentPageItemType.Text:
      return data.text;
    case ContentPageItemType.HTML:
      return data.html;
  }
}

function readContentPage(data: any): ContentPage {
  const { root_items, url, name, page_title, meta_description } = data;
  return {
    name: name,
    url: url,
    pageTitle: page_title,
    metaDescription: meta_description,
    items: readContentPageItemCollection(root_items),
    pageType: "ContentPage",
  };
}

export async function loadContentPage(url: string): Promise<ContentPage> {
  const response = await callAPI(
    `/content/content-pages/?url=${encodeURIComponent(url)}`,
    "GET"
  );
  return readContentPage(response);
}

function readArticle(data: any): Article {
  const { date, hero_image, url, ...props } = data;
  return {
    date: DateTime.parse(date),
    heroImage: hero_image ? new URL(hero_image) : null,
    url: new URL(url),
    ...props,
  } as Article;
}

export async function loadArticles(): Promise<Article[]> {
  const response = await callAPI("/content/articles/", "GET");
  return response.map(readArticle);
}

function readAward(data: any): Award {
  const { date, logo, url, ...props } = data;
  return {
    date: DateTime.parse(date),
    logo: logo ? new URL(logo) : null,
    url: new URL(url),
    ...props,
  } as Award;
}

export async function loadAwards(): Promise<Award[]> {
  const response = await callAPI("/content/awards/", "GET");
  return response.map(readAward);
}

export async function requestBlogReport(
  blogPostCode: string,
  request: ReportRequest
): Promise<URL> {
  const data = {
    first_name: request.firstName,
    last_name: request.lastName,
    phone: request.phone,
    email: request.email,
    company: request.company,
    tracking_id: getTrackingID(),
  };
  const response = await callAPI(
    `/content/blog/posts/${blogPostCode}/report/request/`,
    "PUT",
    data
  );
  return new URL(response.url);
}

function readBlogPostQuote(data: any): BlogPostQuote {
  return {
    text: data.text,
    customerName: data.customer_name,
    customerCompany: data.customer_company,
  };
}

function readBlogPostContent(data: any): BlogPostContent {
  const { body_top, body_bottom, quote, images, ...props } = data;
  return {
    bodyTop: body_top,
    bodyBottom: body_bottom,
    quote: quote ? readBlogPostQuote(quote) : null,
    images: images ? images.map((i: string) => new URL(i)) : [],
    ...props,
  };
}

function readBlogPost(data: any): BlogPost {
  const { published, hero_image, url, has_report, content, ...props } = data;
  return {
    published: DateTime.parse(published),
    heroImage: hero_image ? new URL(hero_image) : null,
    url: url ? new URL(url) : null,
    hasReport: !!has_report,
    content: content ? readBlogPostContent(content) : null,
    ...props,
  } as BlogPost;
}

export async function loadBlogPosts(): Promise<BlogPost[]> {
  const response = await callAPI("/content/blog/posts/", "GET");
  return response.map(readBlogPost);
}

export async function loadBlogPost(code: string): Promise<BlogPost> {
  const response = await callAPI(`/content/blog/posts/${code}/`, "GET");
  return readBlogPost(response);
}

function readTestimonial(data: any): Testimonial {
  const { hero_image, ...props } = data;
  return {
    heroImage: hero_image ? new URL(hero_image) : null,
    ...props,
  } as Testimonial;
}

export async function loadTestimonials(): Promise<Testimonial[]> {
  let testimonials = getCacheItem(TESTIMONIALS_CACHE_KEY);
  if (testimonials) {
    return testimonials;
  }
  const response = await callAPI("/content/testimonials/", "GET");
  testimonials = response.map(readTestimonial);
  setCacheItem(TESTIMONIALS_CACHE_KEY, testimonials);
  return testimonials;
}

function readSpaceHighlight(data: any): SpaceHighlight {
  const { hero_image, ...props } = data;
  return {
    heroImage: new URL(hero_image),
    ...props,
  } as SpaceHighlight;
}

export async function loadSpaceHighlights(): Promise<SpaceHighlight[]> {
  let highlights = getCacheItem(SPACE_HIGHLIGHTS_CACHE_KEY);
  if (highlights) {
    return highlights;
  }
  const response = await callAPI("/content/space-highlights/", "GET");
  highlights = response.map(readSpaceHighlight);
  setCacheItem(SPACE_HIGHLIGHTS_CACHE_KEY, highlights);
  return highlights;
}

function readSuccessStory(data: any): Testimonial {
  const { hero_image, ...props } = data;
  return {
    heroImage: new URL(hero_image),
    ...props,
  } as SuccessStory;
}

export async function loadSuccessStories(): Promise<SuccessStory[]> {
  let successStories = getCacheItem(SUCCESS_STORIES_CACHE_KEY);
  if (successStories) {
    return successStories;
  }
  const response = await callAPI("/content/success-stories/", "GET");
  successStories = response.map(readSuccessStory);
  setCacheItem(SUCCESS_STORIES_CACHE_KEY, successStories);
  return successStories;
}
